import { useMemo } from "react";
import { useApiConfig, installServerErrorInterceptor, SERVER_ERROR_MESSAGE } from "common-fe";
import {
  Configuration,
  ShowsApi,
  ClassesApi,
  RidesApi,
  ScoreSheetsApi,
  TestsApi,
  PeopleApi,
  HorsesApi,
  JudgesApi,
  CatalogApi,
  RingStateApi,
  PresenceApi,
} from "score-common";

// Single place that turns the current auth session into instantiated
// score-common API clients. `useApiConfig` (common-fe) stamps the Cognito
// bearer token (and, if an ActiveOrgProvider were mounted, the X-Org-Id
// header) onto every request; score-api otherwise resolves the org from the
// token's `orgs` claim. Base URL comes from VITE_SCORE_API_BASE_URL.
const SCORE_API_BASE_URL = import.meta.env.VITE_SCORE_API_BASE_URL;

// Rewrite every failed API call's error.message to a generic "Server Error"
// (raw detail kept on the error for console logging) so backend codes never
// reach the UI. Installed once on the shared axios singleton at module load.
installServerErrorInterceptor();

export function useScoreApi() {
  const buildConfig = useApiConfig();
  return useMemo(() => {
    const config = new Configuration(buildConfig(SCORE_API_BASE_URL));
    return {
      shows: new ShowsApi(config),
      classes: new ClassesApi(config),
      rides: new RidesApi(config),
      scoreSheets: new ScoreSheetsApi(config),
      tests: new TestsApi(config),
      people: new PeopleApi(config),
      horses: new HorsesApi(config),
      judges: new JudgesApi(config),
      // card #1664 — GET /catalog, the global test-def catalog (org-agnostic,
      // allowNoOrg — see apps/score-api/src/handlers/catalog.js). Same
      // Configuration as everything else here.
      catalog: new CatalogApi(config),
      // #1935 (docs/score/ring-state.md) — Start class / ring-level Break /
      // End of day. score:set_ring_state is already granted to score_scribe
      // (packages/score-common/src/permissions/index.js).
      ringState: new RingStateApi(config),
      // #2364 — the presence heartbeat SENDER (POST /presence/heartbeat,
      // #2362). Called directly by usePresenceHeartbeat.js, never through
      // ScoreContext's syncEngine/apiAdapter: the fire-and-forget guarantee
      // depends on this write never entering the scoring outbox.
      presence: new PresenceApi(config),
    };
  }, [buildConfig]);
}

// JSend unwrap: score-api wraps every success as { status: "success", data }.
// The generated client returns the whole envelope as response.data, so callers
// want response.data.data. Throws on a non-success envelope so a hook's catch
// surfaces it.
export function unwrap(response) {
  const body = response?.data;
  if (!body || body.status !== "success") {
    // A 2xx response carrying a JSend failure envelope — don't leak the backend
    // message/code into the UI; log it and throw the generic message.
    console.error("[score-api]", body?.data?.message || body?.code, body);
    throw new Error(SERVER_ERROR_MESSAGE);
  }
  return body.data;
}
